import { VIDEO_QUALITY_PRESETS } from '../store/devices.store'
import { AUDIO_PRESETS, DEFAULT_AUDIO_PRESET } from '../constants/audio-presets'
import { isWeakDevice } from './device-capability'
import type { AudioFormat, AudioProcessing } from '../constants/audio-presets'

/**
 * The one owner of the camera and the microphone.
 *
 * Capture used to be owned by React components, and their lifetimes do not
 * match the hardware's. The lobby preview died at join, so the meeting had to
 * open the camera again; tiles needed a picture and had no route to the
 * published track, so a third capture appeared behind a refcount; a permission
 * dialog opened a fourth just to find out whether it was allowed to. Four
 * places decided what "the user's camera" meant, with four different sets of
 * constraints, and the only thing keeping them apart was that they mostly did
 * not run at once.
 *
 * This module is deliberately outside React. It holds at most ONE camera track
 * and ONE microphone track, hands the same track to every consumer, and stops a
 * device only when the last consumer lets go. `capture()` below is the single
 * `getUserMedia` call for the main devices in the whole app — the only others
 * are the additional-video-input path (a genuinely different device) and the
 * messenger's photo/voice-note capture, which is a separate feature.
 *
 * The same pattern is already load-bearing next door in `segmentation-source`,
 * for the same reason: sharing one expensive resource beats coordinating
 * several copies of it.
 */

export type MediaKind = 'audio' | 'video'

/**
 * Notified when a kind's track is REPLACED — a device switch — or ends.
 *
 * May be async, and the manager waits for every listener before retiring the
 * old track, so a subscriber is never left rendering something that has already
 * ended. A consumer which drives switches itself by calling `setDevice` rather
 * than subscribing does NOT get that ordering — the old track is already
 * stopped by the time the call returns. That is deliberate for the publisher,
 * whose device switch has always shown a frozen frame while the compositor is
 * rebuilt anyway.
 */
type TrackListener = (track: MediaStreamTrack | null) => void | Promise<void>

type VideoQuality = { width: number; height: number; frameRate: number }

type Slot = {
  track: MediaStreamTrack | null
  refCount: number
  deviceId: string | null
  listeners: Set<TrackListener>
  /** In flight, so simultaneous callers share one request rather than racing. */
  pending: Promise<MediaStreamTrack | null> | null
  /** Release is deferred, so a remount does not cost a device restart. */
  retireTimer: ReturnType<typeof setTimeout> | null
  /**
   * A replacement is being opened after the device died.
   *
   * Re-entrancy is not hypothetical here: recovery opens a device, and if that
   * device is also on its way out its own `ended` fires while we are still
   * inside the first attempt. Without this the two chase each other.
   */
  recovering: boolean
  /**
   * The camera a gapless switch is moving away from. It is still the slot's
   * track while the new one opens, and some platforms end it the moment a
   * second camera starts — that is the switch, not a disconnection.
   */
  replacing: MediaStreamTrack | null
  /**
   * Bumped by `stopAll`. An `acquire` that started before the close and lands
   * after it belongs to a session that has gone, so it stops what it opened
   * instead of leaving a device live for nobody.
   */
  generation: number
}

/**
 * How long a device stays open with no consumers.
 *
 * Long enough to survive a remount (React's dev-mode double-invoke, a route
 * transition, the lobby handing over to the meeting), short enough that a user
 * who leaves does not keep their camera light on while they wonder why.
 */
const RETIRE_GRACE_MS = 1500

function newSlot(): Slot {
  return {
    track: null,
    refCount: 0,
    deviceId: null,
    listeners: new Set(),
    pending: null,
    retireTimer: null,
    recovering: false,
    replacing: null,
    generation: 0,
  }
}

const slots: Record<MediaKind, Slot> = { audio: newSlot(), video: newSlot() }

/** Current capture settings. Held here because this module decides them now. */
let videoQuality: VideoQuality = { ...VIDEO_QUALITY_PRESETS['High'] }
let audioProcessing: AudioProcessing = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
}
/** RNNoise and the browser's own suppressor must never both run — see below. */
let noiseCancellation = AUDIO_PRESETS[DEFAULT_AUDIO_PRESET].noiseCancellation
/** Capture format from the selected audio preset. */
let audioFormat: AudioFormat = { ...AUDIO_PRESETS[DEFAULT_AUDIO_PRESET].format }



/** Capture constraints for the current preset, floored by a weak device. */
/** What a camera says it can do, or null when the browser will not say. */
export type CameraCapabilities = {
  maxWidth: number
  maxHeight: number
  maxFrameRate: number
} | null

/**
 * The selected camera's own limits, read from the live track.
 *
 * getCapabilities is the only honest answer to "does this camera support
 * 2160p". A constraint request cannot tell you: `ideal` is declined silently,
 * so asking for 4K on a 1080p webcam returns 1080p with no error anywhere.
 *
 * Not universally implemented — Firefox omitted it for years — hence the null.
 * A null means unknown, never unsupported.
 */
export function cameraCapabilities(track: MediaStreamTrack): CameraCapabilities {
  if (typeof track.getCapabilities !== 'function') return null
  try {
    const c = track.getCapabilities()
    if (!c.width || !c.height) return null
    return {
      maxWidth: c.width.max ?? 0,
      maxHeight: c.height.max ?? 0,
      maxFrameRate: c.frameRate?.max ?? 0,
    }
  } catch {
    return null
  }
}

/** Outcome of a quality change, including why it did not take. */
export type VideoQualityResult = {
  applied: boolean
  reason: 'ok' | 'no-track' | 'rejected' | 'clamped-by-camera'
  requested?: { width: number; height: number }
  actual?: { width?: number; height?: number; frameRate?: number }
  capabilities?: CameraCapabilities
}

function videoConstraints(deviceId: string | null): MediaTrackConstraints {
  const weak = isWeakDevice()
  const width = weak ? Math.min(videoQuality.width, 640) : videoQuality.width
  const height = weak ? Math.min(videoQuality.height, 360) : videoQuality.height
  const frameRate = weak ? Math.min(videoQuality.frameRate, 15) : videoQuality.frameRate
  return {
    // `exact`, never `ideal`. `ideal` is a preference the browser may quietly
    // decline, and declining looks identical to succeeding — which is exactly
    // how a switch to a Bluetooth headset came back as the built-in device with
    // every layer above reporting success. A device we cannot have must fail
    // loudly; `captureWithFallback` decides, in one place, when falling back to
    // the system default is acceptable.
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    width: { ideal: width, max: width },
    height: { ideal: height, max: height },
    frameRate: { ideal: frameRate, max: frameRate },
  }
}

/**
 * Microphone constraints, with the two denoisers kept apart.
 *
 * RNNoise and the browser's `noiseSuppression` must never both run: two
 * suppressors in series chew up quiet speech and sibilants, so the result is
 * worse than either alone. When RNNoise is on the browser filter goes off and
 * the Web Audio graph does the work.
 */
function audioConstraints(deviceId: string | null): MediaTrackConstraints {
  return {
    // `exact` — see the note in videoConstraints.
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    echoCancellation: audioProcessing.echoCancellation,
    noiseSuppression: noiseCancellation ? false : audioProcessing.noiseSuppression,
    autoGainControl: audioProcessing.autoGainControl,
    // From the preset rather than hardcoded. `sampleRate` was pinned at 48000
    // here while the Settings dropdown offering six rates was commented out for
    // want of anything to write to — these are the numbers it now writes.
    //
    // Deliberately NOT `exact`: a device that cannot do 24 kHz should be opened
    // at whatever it can do and resampled, not refused. Substitution is the bug
    // for a DEVICE, where you asked for a specific piece of hardware; a sample
    // rate is a preference about the same hardware.
    sampleRate: audioFormat.sampleRate,
    channelCount: audioFormat.channelCount,
    sampleSize: audioFormat.sampleSize,
  }
}

/**
 * Is the microphone doing the echo, noise and gain processing we now ask for?
 *
 * Read from the track's own settings, because Chromium accepts
 * `applyConstraints` for these three on a running microphone and then ignores
 * it (crbug.com/796964) — no error, nothing changed. Measured 2026-10-21 on
 * Chrome 155 / Android: opened with noiseSuppression off, asked for on, still
 * off, and the reverse. So switching noise cancellation on mid-call left the
 * browser suppressor running under RNNoise, and switching it off left no
 * suppression at all. Old InMeet re-opened the microphone for the same reason.
 *
 * A setting the browser does not report is taken as matching: there is nothing
 * to compare, and re-opening on every change would cost audio for nothing.
 */
function processingMatches(track: MediaStreamTrack): boolean {
  const actual = track.getSettings()
  const wanted = audioConstraints(null)
  return (['echoCancellation', 'noiseSuppression', 'autoGainControl'] as const).every(
    (key) => typeof actual[key] !== 'boolean' || actual[key] === wanted[key],
  )
}

/**
 * Re-open the same microphone with the current processing, and hand it over.
 *
 * CLOSE FIRST, then open — the same order as a microphone switch, for a
 * different reason. Chromium gives a second capture of a device that is
 * already open the SAME source, processing and all: measured 2026-10-21 on
 * Chrome 155 / Android, with the running microphone suppressing, a fresh
 * `getUserMedia` for the same device asking for noiseSuppression off — even as
 * `exact` — came back suppressing. Only a closed device opens fresh. The cost
 * is a short gap in what the room hears, which is what old InMeet paid too.
 *
 * Subscribers (the publisher, which rebuilds its denoiser on the new track)
 * hear about it the way they hear about a device switch.
 *
 * Returns whether the new track has the processing asked for. If the device
 * will not open again, the same request is retried once and then the system
 * default is taken: worse processing, or another microphone, is better than
 * no microphone.
 */
async function reopenMicrophone(old: MediaStreamTrack): Promise<boolean> {
  const slot = slots.audio
  if (slot.recovering || slot.pending) return false
  const generation = slot.generation
  const wasEnabled = old.enabled
  const deviceId = slot.deviceId ?? old.getSettings().deviceId ?? null
  trace('audio', 'REOPENING (audio processing the running track would not change)', {
    wanted: audioConstraints(null),
    actual: old.getSettings(),
  })

  const open = async (id: string | null): Promise<MediaStreamTrack | null> => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints(id) })
    return stream.getAudioTracks()[0] ?? null
  }

  // `acquire` during the gap waits for this rather than opening a second one.
  let settle: (track: MediaStreamTrack | null) => void = () => {}
  slot.pending = new Promise((resolve) => {
    settle = resolve
  })
  old.stop()
  slot.track = null

  let next: MediaStreamTrack | null = null
  let openedId: string | null = deviceId
  try {
    for (const id of [deviceId, deviceId, null]) {
      try {
        next = await open(id)
        openedId = id
        if (next) break
      } catch (err) {
        trace('audio', 'reopen for audio processing FAILED', {
          deviceId: id,
          error: (err as Error).message,
        })
      }
    }
    // Left, or the microphone was closed, while this was opening.
    if (next && slot.generation !== generation) {
      next.stop()
      next = null
    }
    if (next) {
      next.enabled = wasEnabled
      adopt('audio', next)
      slot.deviceId = next.getSettings().deviceId ?? deviceId
    }
  } finally {
    slot.pending = null
    settle(next)
  }
  if (!next) {
    if (slot.generation === generation) {
      reportIssue({
        kind: 'audio',
        reason: 'switch-failed',
        message: 'The microphone could not be restarted with the new audio settings.',
      })
    }
    return false
  }
  if (deviceId && openedId === null) {
    reportIssue({
      kind: 'audio',
      reason: 'fallback',
      message: 'That microphone would not restart. Using the system default instead.',
      requestedDeviceId: deviceId,
    })
  }
  await notify('audio', next)
  return processingMatches(next)
}

/**
 * THE `getUserMedia` call for the camera and the microphone.
 *
 * Everything else in this module funnels through here, which is the entire
 * point of the module. Both kinds are requested together when both are wanted
 * so a first-time visitor sees ONE permission prompt rather than two — the
 * property the old preview hook went out of its way to preserve, kept here
 * because here is where it can actually be guaranteed.
 */
async function capture(want: { audio: boolean; video: boolean }): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: want.audio ? audioConstraints(slots.audio.deviceId) : false,
    video: want.video ? videoConstraints(slots.video.deviceId) : false,
  })
}

/** Was this rejection "that exact device is not available"? */
function overconstrained(err: unknown): boolean {
  return err instanceof DOMException && err.name === 'OverconstrainedError'
}

/**
 * Capture, and decide what an unavailable device means.
 *
 * `exact` makes a missing device an error rather than a silent substitution,
 * which is what we want — but the two situations it covers are not the same:
 *
 *  - **Opening for the first time.** The stored selection routinely points at a
 *    headset that was unplugged days ago. Refusing to start would cost you the
 *    meeting over a stale preference, so we drop the id, take the system
 *    default, and SAY SO.
 *  - **Switching on purpose.** You just picked that device. Substituting
 *    another one is the bug this whole change exists to remove, so the failure
 *    propagates and the caller tells you.
 */
async function captureWithFallback(
  want: { audio: boolean; video: boolean },
  allowFallback: boolean,
): Promise<MediaStream> {
  try {
    return await capture(want)
  } catch (err) {
    if (!allowFallback || !overconstrained(err)) throw err
    const dropped = { audio: slots.audio.deviceId, video: slots.video.deviceId }
    if (want.audio) slots.audio.deviceId = null
    if (want.video) slots.video.deviceId = null
    const stream = await capture(want)
    reportIssue({
      kind: want.audio && !want.video ? 'audio' : 'video',
      reason: 'fallback',
      message:
        'The saved device is no longer available. Using the system default instead.',
      requestedDeviceId: (want.audio ? dropped.audio : dropped.video) ?? undefined,
    })
    return stream
  }
}

export type DeviceIssue = {
  kind: MediaKind
  /**
   * `fallback` — a stale selection was dropped for the system default.
   * `switch-failed` — you asked for a device and did not get it.
   * `disconnected` — a device in use went away on its own.
   * `in-use` — the device would not start because something else holds it.
   */
  reason: 'fallback' | 'switch-failed' | 'disconnected' | 'in-use'
  message: string
  requestedDeviceId?: string
  actualDeviceId?: string
  /**
   * Present on `disconnected` when we opened a replacement ourselves.
   *
   * Its absence is the signal that the user has to do something; its presence
   * is the signal that they do not. Telling somebody to pick another
   * microphone while we are already switching them to one is worse than saying
   * nothing, because it describes an app that is not the one in front of them.
   */
  recovered?: { deviceId: string | null; label: string }
}

const issueListeners = new Set<(issue: DeviceIssue) => void>()

/**
 * Tell somebody. This module has no UI, and the reason a wrong microphone went
 * unnoticed for so long is that every failure here was written to a debug log
 * nobody had enabled.
 */
function reportIssue(issue: DeviceIssue): void {
  issueListeners.forEach((listener) => {
    try {
      listener(issue)
    } catch {
      // One bad listener must not silence the rest.
    }
  })
}

/**
 * The browser may use this device, but it would not start.
 *
 * Every engine words it differently — Chrome `NotReadableError` ("Could not
 * start video source", "Device in use"), Firefox `AbortError` ("Starting
 * videoinput failed"), old Chrome `TrackStartError` — and on a desktop it
 * nearly always means another application is holding the device. Windows gives
 * a camera to one process at a time, so the same meeting open in a second
 * browser is enough. Never a refusal: all three come after the grant.
 */
export function deviceInUse(err: unknown): boolean {
  const name = (err as { name?: string } | null)?.name
  return name === 'NotReadableError' || name === 'AbortError' || name === 'TrackStartError'
}

/**
 * Say which device is taken, and by what.
 *
 * Without this a camera held by another browser came back as a lit camera
 * button over a black tile: the capture failed, the failure was recorded where
 * no one could see it, and nothing told the user there was anything to do.
 */
function reportInUse(kind: MediaKind): void {
  reportIssue({
    kind,
    reason: 'in-use',
    message:
      kind === 'audio'
        ? 'Your microphone is being used by another browser or app. Turn it off there to use it here.'
        : 'Your camera is being used by another browser or app. Turn it off there to use it here.',
  })
}

/**
 * Every open and close of a device, with the stack that caused it.
 *
 * "The indicator light is still on" is not a report anyone can act on: the
 * camera has five ways in — `prime`, `acquire`, `setDevice`'s repair branch,
 * `recoverAfterLoss`, and a re-`adopt` after a switch — and from the outside
 * they are indistinguishable. `debugState()` says WHAT is held; this says WHO
 * took it and when, which is the half that names the culprit.
 *
 * It matters most in the state `stop()` leaves behind: an empty slot with
 * claims still outstanding. Both `setDevice` and `recoverAfterLoss` read that
 * as a device that died under a holder and repair it by opening the camera
 * again — correct when the hardware really did go away, a reopening loop when
 * it was closed on purpose.
 *
 * OPT-IN everywhere: `localStorage.setItem('devices:trace','1')`, then reload.
 *
 * It used to switch itself on for every dev build, which made it permanent
 * noise rather than an instrument — a line per acquire, release, retire and
 * reopen, each with four frames of stack, in every local session whether anyone
 * was looking at devices or not. The flag is the whole point: turn it on when a
 * camera question needs answering, and it costs nothing the rest of the time
 * because `trace` returns before building the stack.
 */
const TRACE = (() => {
  try {
    return localStorage.getItem('devices:trace') === '1'
  } catch {
    return false // private mode / storage disabled
  }
})()

function trace(kind: MediaKind, event: string, extra?: Record<string, unknown>): void {
  if (!TRACE) return
  const slot = slots[kind]
  // The frames just past this function are the caller worth reading; below
  // them is React's scheduler, which names nobody.
  const stack = (new Error().stack ?? '').split('\n').slice(2, 6).join('\n')
  console.info(
    `[devices] ${kind} ${event}`,
    { refCount: slot.refCount, hasTrack: !!slot.track, ...extra },
    '\n' + stack,
  )
}

/** Which physical device a track was capturing, read while it was still live. */
type LostDevice = { deviceId: string | null; groupId: string | null }

function adopt(kind: MediaKind, track: MediaStreamTrack | null): void {
  if (track) trace(kind, 'OPENED', { label: track.label })
  const slot = slots[kind]
  slot.track = track
  if (!track) return
  // Read now, not in the `ended` handler: an ended track is not obliged to keep
  // reporting the device it was on, and that is the one fact recovery needs.
  const settings = track.getSettings()
  const lost: LostDevice = {
    deviceId: settings.deviceId ?? null,
    groupId: settings.groupId ?? null,
  }
  // A device pulled out from under us, or revoked. Consumers hear about it the
  // same way they hear about a switch, so nobody is left rendering a dead track.
  track.addEventListener('ended', () => {
    // Only if this is still the current track: a device switch ends the old one
    // on purpose, and that is not a disconnection.
    if (slots[kind].track !== track || slots[kind].replacing === track) return
    slots[kind].track = null
    const claimed = slots[kind].refCount > 0
    void notify(kind, null)
    // Somebody was using it. A microphone that dies mid-call is otherwise
    // completely silent — in both senses — so this is where the replacement
    // gets opened, and where the user gets told which one it is.
    if (claimed) void recoverAfterLoss(kind, lost)
  })
}

/**
 * Is the device we reopened the same physical hardware as the one that ended?
 *
 * `groupId` is the physical device; `deviceId` alone cannot say, because the
 * `default` alias keeps its id while the hardware behind it changes. Without a
 * groupId on both sides (Firefox, tests) a matching non-`default` id is the
 * best evidence there is.
 */
function samePhysicalDevice(lost: LostDevice, opened: MediaStreamTrack): boolean {
  const now = opened.getSettings()
  if (lost.groupId && now.groupId) return lost.groupId === now.groupId
  return !!lost.deviceId && lost.deviceId !== 'default' && lost.deviceId === now.deviceId
}

/**
 * A device somebody was using died. Open its replacement.
 *
 * This behaviour already existed, and worked, and nobody had written it. Turning
 * a Bluetooth headset off mid-call did land you on the built-in microphone — via
 * a `devicechange` listener in a hook, which re-enumerated, which let the store
 * notice its saved id had vanished, which rewrote the selection, which re-ran an
 * effect in the meeting, which called `setDevice`. Three links across three
 * unrelated files, only the last of them written on purpose.
 *
 * Chains like that fail in ways nobody predicts, and this one had four:
 *
 *  - The replacement id had to DIFFER from the dead one. One microphone in the
 *    machine, or a device that comes back under the same id, and the store write
 *    is a no-op — so nothing downstream re-ran.
 *  - Chrome had to drop the entry from `enumerateDevices` promptly. While it
 *    lingers the store still believes the selection is present and repairs
 *    nothing.
 *  - An empty selection (`''`, the state before the user has ever picked)
 *    reached `setDevice(kind, null)`, which means "no preference", which
 *    returned the track that had just ended.
 *  - The `devicechange` listener was mounted by accident — it is live in a
 *    meeting because the bottom bar's mic control happens to enumerate devices.
 *    A layout without that control loses recovery and nothing says so.
 *
 * So recovery moves here, where the death is actually observed and none of those
 * four things have to be true. The store repair still runs, and is now a second
 * way in rather than the only one: by the time it arrives `setDevice` is already
 * on the recovered device and returns early.
 *
 * A track ending is not proof its device left. Unplugging a USB camera resets
 * the hub it shared with a USB microphone, and a mic captured through the
 * `default` alias can end when the system re-picks its default — in both cases
 * the microphone is still plugged in. Reporting that as "Microphone
 * disconnected" told someone who had only pulled out their camera that their
 * mic had gone. So the same device is tried first, and the user hears about it
 * only when the hardware they are now on is not the hardware they were on.
 */
async function recoverAfterLoss(kind: MediaKind, lost: LostDevice | null = null): Promise<void> {
  const slot = slots[kind]
  if (slot.recovering) return
  slot.recovering = true
  trace(kind, 'REOPENING (recoverAfterLoss: the track ended under a holder)')
  // Read before the capture, compared after it. `stop` and `stopAll` bump this
  // precisely so an open already in flight knows the session it belongs to has
  // been closed — see the guard below.
  const generation = slot.generation
  try {
    const want = { audio: kind === 'audio', video: kind === 'video' }
    let stream: MediaStream | null = null
    // The same device first, by `exact` id. If it really is gone this refuses
    // at once, which is the honest test of "still here" — `enumerateDevices`
    // can go on listing a device for a moment after it has been unplugged.
    if (lost?.deviceId) {
      slot.deviceId = lost.deviceId
      try {
        stream = await capture(want)
      } catch {
        stream = null
      }
    }
    // Then drop the dead id. It is the one thing we know to be wrong, and
    // `exact` on it would refuse every attempt — the constraint that makes a
    // deliberate switch honest makes a blind retry impossible. With no id the
    // browser gives us the system default, which after a headset is switched
    // off is the device the user is about to talk into.
    if (!stream) {
      slot.deviceId = null
      stream = await capture(want)
    }
    const opened =
      (kind === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0]) ?? null
    if (!opened) {
      stream.getTracks().forEach((t) => t.stop())
      throw new Error(`no ${kind} device to recover onto`)
    }

    // The ground can move while `getUserMedia` resolves. Everyone may have let
    // go — the user left the meeting — or a deliberate switch may have got there
    // first and filled the slot. Either way what we just opened is not wanted,
    // and leaving it running would hold a device nobody asked for.
    //
    // The generation half is what makes a LEAVE count as moving ground, and it
    // was the one in-flight open that could not be cancelled. `stopAll` closes
    // the devices and bumps the generation, but deliberately leaves `refCount`
    // alone — every holder still owes its release — so a recovery that started
    // just before the user left came back to `refCount > 0` and an empty slot,
    // read that as "still wanted", and adopted a freshly opened camera into a
    // meeting that no longer exists. Nothing runs after `stopAll` to stop it,
    // so the indicator stayed on for the life of the tab.
    //
    // Same test its siblings already make (`acquire`, and `stop`'s own comment
    // about what the bump is for); this path simply never read it.
    if (slot.refCount === 0 || slot.track || slot.generation !== generation) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }

    adopt(kind, opened)
    slot.deviceId = opened.getSettings().deviceId ?? null
    await notify(kind, opened)
    // Back on the hardware we lost: nothing was disconnected from the user's
    // point of view, and the selection in the store is already right.
    if (lost && samePhysicalDevice(lost, opened)) {
      trace(kind, 'RECOVERED onto the same device — not reported', { label: opened.label })
      return
    }
    reportIssue({
      kind,
      reason: 'disconnected',
      message: `Switched to ${opened.label || 'the system default'}.`,
      actualDeviceId: slot.deviceId ?? undefined,
      recovered: { deviceId: slot.deviceId, label: opened.label },
    })
  } catch (err) {
    // Still here, but something else has it now. Picking another device is not
    // the fix for that; freeing this one is.
    if (deviceInUse(err)) {
      reportInUse(kind)
      return
    }
    // Nothing to move to: the last microphone in the machine, or permission
    // revoked along with the device. Now — and only now — is asking the user to
    // choose the right thing to do.
    reportIssue({
      kind,
      reason: 'disconnected',
      message:
        kind === 'audio'
          ? 'Your microphone stopped. Pick another one from the microphone menu.'
          : 'Your camera stopped. Pick another one from the camera menu.',
    })
  } finally {
    slot.recovering = false
  }
}

async function notify(kind: MediaKind, track: MediaStreamTrack | null): Promise<void> {
  const listeners = [...slots[kind].listeners]
  await Promise.all(
    listeners.map(async (listener) => {
      try {
        await listener(track)
      } catch {
        // A consumer that cannot handle a swap must not stop the others from
        // hearing about it, and must not leave the old track un-retired.
      }
    }),
  )
}

/** Stop a device that no consumer has claimed by the time the clock runs out. */
function scheduleRetireIfUnclaimed(kind: MediaKind): void {
  const slot = slots[kind]
  if (!slot.track || slot.refCount > 0 || slot.retireTimer) return
  slot.retireTimer = setTimeout(() => {
    slot.retireTimer = null
    if (slot.refCount > 0 || !slot.track) return
    trace(kind, 'CLOSED (retired — nobody claimed it)')
    slot.track.stop()
    slot.track = null
    void notify(kind, null)
  }, RETIRE_GRACE_MS)
}

function cancelRetire(kind: MediaKind): void {
  const slot = slots[kind]
  if (!slot.retireTimer) return
  clearTimeout(slot.retireTimer)
  slot.retireTimer = null
}

/** How long a new camera may take to show its first frame before we swap anyway. */
const FIRST_FRAME_TIMEOUT_MS = 1500

/**
 * Resolve once a freshly opened camera is actually delivering pictures.
 *
 * `getUserMedia` resolves when the device has started, not when it has produced
 * a frame — a USB webcam commonly spends a few hundred milliseconds on exposure
 * after that. Swapping onto it early shows black where the old camera was still
 * showing you perfectly well. Bounded, and never rejects: a camera slow to show
 * a frame is still the camera that was asked for.
 */
function firstFrame(track: MediaStreamTrack): Promise<void> {
  // No DOM (tests, workers): nothing to watch with, and nothing to show anyway.
  if (typeof document === 'undefined' || typeof MediaStream === 'undefined') {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    const probe = document.createElement('video')
    probe.muted = true
    probe.playsInline = true
    const finish = () => {
      clearTimeout(timer)
      probe.removeEventListener('loadeddata', finish)
      probe.srcObject = null
      resolve()
    }
    const timer = setTimeout(finish, FIRST_FRAME_TIMEOUT_MS)
    probe.addEventListener('loadeddata', finish)
    probe.srcObject = new MediaStream([track])
    void probe.play().catch(() => {})
  })
}

/**
 * The new camera could not start while the old one was running.
 *
 * Phones that cannot run front and back cameras together, and USB buses without
 * the bandwidth for two webcams, refuse the second capture this way.
 */
function cannotOpenAlongside(err: unknown): boolean {
  // The same refusal as `deviceInUse`; here the other holder is our own camera.
  return deviceInUse(err)
}

async function openCamera(deviceId: string): Promise<MediaStreamTrack> {
  const stream = await captureWithFallback({ audio: false, video: true }, false)
  const track = stream.getVideoTracks()[0] ?? null
  if (!track) {
    stream.getTracks().forEach((t) => t.stop())
    throw new Error('no video track for the selected device')
  }
  // Same distrust as the close-first path in `setDevice`.
  const actual = track.getSettings().deviceId
  if (actual && actual !== deviceId) {
    track.stop()
    throw new Error('browser returned a different video device than the one requested')
  }
  return track
}

/**
 * Switch cameras with no gap: open the new one while the old keeps running,
 * move every subscriber across, and only then stop the old one.
 *
 * `setDevice` closes before it opens, and for a microphone it has to (see the
 * note there). For a camera that ordering WAS the switching delay: the old
 * camera went dark the moment a new one was picked and stayed dark — for you
 * and for the room — for as long as the new one took to open and warm up, which
 * is one to two seconds for a typical USB webcam. Two cameras are two devices;
 * on a desktop there is no room to make.
 *
 * Returns 'close-first' when the old camera has to go before the new one can
 * start, and `setDevice` falls back to the ordering it has always used.
 */
async function switchCameraWhileRunning(
  current: MediaStreamTrack,
  deviceId: string,
  previousDeviceId: string | null,
): Promise<MediaStreamTrack | null | 'close-first'> {
  const slot = slots.video
  const generation = slot.generation
  slot.replacing = current
  try {
    let next: MediaStreamTrack
    try {
      next = await openCamera(deviceId)
    } catch (err) {
      // Turned off while we were opening: leave it off, whatever went wrong.
      if (slot.generation !== generation) return null
      // Some platforms end the old camera when a second one starts, so there
      // may be nothing left to keep running. Either way the close-first path,
      // with its rollback, is the right one from here.
      if (cannotOpenAlongside(err) || current.readyState === 'ended') return 'close-first'
      // The old camera never stopped, so there is nothing to roll back to — a
      // failed switch here costs the user nothing but the message.
      slot.deviceId = previousDeviceId
      reportIssue({
        kind: 'video',
        reason: 'switch-failed',
        message: 'Could not switch to that camera. Still using the previous one.',
        requestedDeviceId: deviceId,
      })
      throw err
    }

    // Swap onto pictures, not onto a camera still adjusting its exposure.
    await firstFrame(next)

    // The camera was turned off while the new one warmed up. Leave it off.
    if (slot.generation !== generation) {
      next.stop()
      return null
    }
    // A second request for this same camera, racing this one, landed first.
    // Adopting ours too would leave one of the two running for nobody.
    const occupant = slot.track
    if (
      occupant &&
      occupant !== current &&
      occupant.readyState === 'live' &&
      occupant.getSettings().deviceId === deviceId
    ) {
      next.stop()
      return occupant
    }

    // Inherit camera-off, so the new device is never live while you are off.
    next.enabled = current.enabled
    adopt('video', next)
    slot.deviceId = next.getSettings().deviceId ?? deviceId
    // The old camera is still running while everyone moves across, so neither
    // the room nor your own tile ever has nothing to show.
    await notify('video', next)
    if (occupant && occupant !== next) occupant.stop()
    return next
  } finally {
    if (slot.replacing === current) slot.replacing = null
  }
}

export const deviceManager = {
  isSupported(): boolean {
    return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  },

  /**
   * Open both devices in one request, for the single permission prompt.
   *
   * Call before acquiring individually when both are wanted. Anything already
   * open is left alone, so this is safe to call more than once — a second
   * visitor to the lobby joins the capture the first one started.
   */
  async prime(want: { audio: boolean; video: boolean }): Promise<void> {
    const needAudio = want.audio && !slots.audio.track
    const needVideo = want.video && !slots.video.track
    if (!needAudio && !needVideo) return

    const audioGeneration = slots.audio.generation
    const videoGeneration = slots.video.generation
    let stream: MediaStream
    try {
      stream = await captureWithFallback({ audio: needAudio, video: needVideo }, true)
    } catch (err) {
      if (!deviceInUse(err)) throw err
      // A device that would not start is not a refusal, so there is no prompt
      // left for this call to save — and it is no verdict on the OTHER device.
      // One request for two devices fails whole when either cannot start, which
      // is how a camera held by a meeting in another browser took the
      // microphone down with it. Asked for both, it cannot say which one it
      // was, so it opens nothing and leaves that to the per-kind `acquire`
      // that follows every combined prime, which can. Asked for one, it knows.
      if (needAudio && needVideo) return
      reportInUse(needAudio ? 'audio' : 'video')
      throw err
    }
    // Left while the capture (or its permission prompt) was up: what it opened
    // belongs to a session that has been closed. Same test as `acquire`.
    if (slots.audio.generation !== audioGeneration || slots.video.generation !== videoGeneration) {
      stream.getTracks().forEach((t) => t.stop())
      return
    }
    // One request, two owners from here on.
    const audioTrack = stream.getAudioTracks()[0] ?? null
    const videoTrack = stream.getVideoTracks()[0] ?? null
    if (needAudio && audioTrack) {
      adopt('audio', audioTrack)
      slots.audio.deviceId = audioTrack.getSettings().deviceId ?? slots.audio.deviceId
    } else if (audioTrack) {
      audioTrack.stop()
    }
    if (needVideo && videoTrack) {
      adopt('video', videoTrack)
      slots.video.deviceId = videoTrack.getSettings().deviceId ?? slots.video.deviceId
    } else if (videoTrack) {
      videoTrack.stop()
    }

    // Priming opens a device before anyone has claimed it, which is the point —
    // the claims arrive a tick later, from effects. If they never arrive (the
    // page was left while the permission prompt was up) the device would stay
    // open with nobody holding it, so an unclaimed track is put on the same
    // retirement clock a released one is.
    scheduleRetireIfUnclaimed('audio')
    scheduleRetireIfUnclaimed('video')
  },

  /**
   * Claim a device, opening it if nobody has yet.
   *
   * The returned track is SHARED. Never stop it — call `release` instead, and
   * let the last consumer out turn the light off.
   */
  async acquire(kind: MediaKind): Promise<MediaStreamTrack | null> {
    const slot = slots[kind]
    slot.refCount++
    cancelRetire(kind)
    const generation = slot.generation

    if (slot.track && slot.track.readyState === 'live') return slot.track
    if (slot.pending) return slot.pending

    slot.pending = (async () => {
      try {
        const stream = await captureWithFallback(
          { audio: kind === 'audio', video: kind === 'video' },
          true,
        )
        const track =
          (kind === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0]) ?? null
        if (!track) return null
        // Nobody is left wanting it — a toggle flipped back while we were in
        // the permission prompt, or the meeting was left while it was up. Do
        // not leave a device open for no one.
        if (slot.refCount === 0 || slot.generation !== generation) {
          track.stop()
          return null
        }
        adopt(kind, track)
        slot.deviceId = track.getSettings().deviceId ?? slot.deviceId
        return track
      } catch (err) {
        // The claim taken at the top of this call never became a device, so it
        // must not be left on the books. Without this, a denied permission
        // pinned the refcount above zero for the rest of the session and the
        // camera could never be retired.
        slot.refCount = Math.max(0, slot.refCount - 1)
        // Said here rather than by each caller: the lobby preview, the
        // meeting's camera-on and the self tile all come through this door.
        // Not once the device was turned off or the meeting left meanwhile —
        // nobody is waiting on it any more.
        if (deviceInUse(err) && slot.generation === generation) reportInUse(kind)
        throw err
      } finally {
        slot.pending = null
      }
    })()

    return slot.pending
  },

  /**
   * Hold what is ALREADY open, without opening anything.
   *
   * The gap between the lobby letting go and the meeting taking hold is not a
   * remount — it is a join, and it lasts as long as the credentials call, the
   * SFU handshake and the transport setup take. That is easily longer than the
   * retirement grace on a slow network, and the camera would close and re-open
   * exactly where this refactor set out to stop it doing so.
   *
   * So the session takes its own claim the moment it decides to enter the
   * meeting, and holds it until the user leaves. `acquire` would be wrong here:
   * someone joining with their camera off must not have it switched on by the
   * act of joining. Returns whether there was anything to hold, so the caller
   * knows whether it owes a `release`.
   */
  retain(kind: MediaKind): boolean {
    const slot = slots[kind]
    if (!slot.track || slot.track.readyState !== 'live') return false
    slot.refCount++
    cancelRetire(kind)
    return true
  },

  /** Give up a claim. The device stops once the last one is gone. */
  release(kind: MediaKind): void {
    const slot = slots[kind]
    slot.refCount = Math.max(0, slot.refCount - 1)
    if (slot.refCount > 0) return

    // Genuinely stopped when the clock runs out, not merely disabled:
    // `enabled = false` leaves the device open and its indicator light on,
    // which reads as being watched.
    cancelRetire(kind)
    scheduleRetireIfUnclaimed(kind)
  },

  /**
   * Close ONE device now, whatever claims are still outstanding.
   *
   * `release` cannot do this, and that is by design: it decrements a refcount
   * and lets the LAST consumer out turn the light off. For as long as a meeting
   * is running the session itself holds a `retain` claim on both devices (see
   * `VcApp`), so a publisher letting go only ever takes the count from two to
   * one and the hardware stays open. That is exactly right when the camera is
   * merely being paused — the whole point of the claim is to survive the gap
   * between the lobby and the room without a blink.
   *
   * It is wrong when camera-off is supposed to MEAN the device is shut: the
   * indicator light stays on, and the user is told they are off by an app that
   * is still holding the camera.
   *
   * Same shape as `stopAll`, scoped to one kind. The generation bump is what
   * makes an `acquire` already in flight stop whatever it opens instead of
   * adopting it — the toggle can be flipped back while the permission prompt
   * or the capture is still up. `refCount` is deliberately NOT touched: every
   * holder still owes its `release`, and `acquire` re-captures whenever the
   * slot's track is missing or has ended.
   */
  stop(kind: MediaKind): void {
    const slot = slots[kind]
    cancelRetire(kind)
    slot.generation++
    if (!slot.track) return
    // Logged WITH the outstanding claims, because that number is what decides
    // whether the camera stays off: each one is a holder that a later
    // `setDevice` or device-death repair is entitled to reopen it for.
    trace(kind, 'CLOSED (stop — forced past outstanding claims)')
    // `stop()` does not fire `ended`, so the recovery path in `adopt` is not
    // triggered by this — a device we closed on purpose is not a device that
    // died. Subscribers are told directly instead, the same way retirement
    // tells them.
    slot.track.stop()
    slot.track = null
    void notify(kind, null)
  },

  /**
   * Close every device now, whoever still thinks they hold one.
   *
   * `release` is cooperative: it decrements a refcount and lets the last
   * consumer out turn the light off. That is the right shape while a meeting is
   * running, and the wrong one at the moment a user leaves — because it assumes
   * every claim taken was also given back. A single unbalanced claim anywhere
   * (a tile unmounted mid-acquire, a preview whose cleanup did not run, a hook
   * that re-acquired without releasing) leaves the count above zero, the retire
   * timer never fires, and the browser keeps showing the camera-in-use dot on a
   * tab whose meeting ended minutes ago. The user has no way to make it stop
   * short of closing the tab, and no reason to believe they are not being
   * watched.
   *
   * So leaving does not ask. It stops the tracks, drops the counts to zero and
   * tells the subscribers, which is the only way to make "my camera is off when
   * I leave" a guarantee rather than a consequence of every claim being
   * balanced.
   *
   * Safe to call when nothing is open, and safe to call before re-joining:
   * `acquire` re-captures whenever the slot's track is missing or has ended.
   */
  stopAll(): void {
    for (const kind of ['audio', 'video'] as MediaKind[]) {
      const slot = slots[kind]
      cancelRetire(kind)
      // A new generation: an acquire already in flight belongs to the session
      // being closed, so it will stop what it opens rather than leaving a
      // device live with nobody holding it.
      slot.generation++
      // refCount is deliberately NOT reset. Every holder still owes a
      // `release`, and they will make it as their components unmount — a hook
      // from the meeting just left can unmount AFTER the next one has already
      // acquired. Zeroing here turns those owed releases into unmatched
      // decrements against the NEW session's count, which drops it to zero and
      // lets the retirement timer close the camera a second and a half into the
      // meeting the user just rejoined. Leaving the count alone keeps every
      // acquire paired with its own release; the tracks are what has to go.
      const track = slot.track
      if (!track) {
        console.info('[devices] stopAll — nothing open', { kind, refCount: slot.refCount })
        continue
      }
      slot.track = null
      const before = track.readyState
      try {
        track.stop()
      } catch {
        // A track already ended throws on some engines; it is gone either way.
      }
      // Ungated on purpose, and temporary: the camera indicator staying on
      // after a meeting is a report that cannot be reproduced from the code,
      // so this is the line that says whether the track we own was stopped and
      // whether anything else is still holding the device.
      console.info('[devices] stopAll — stopped', {
        kind,
        label: track.label,
        before,
        after: track.readyState,
        refCount: slot.refCount,
      })
      void notify(kind, null)
    }
  },

  /**
   * Every device track this module still holds, for the console.
   *
   * Temporary, alongside the stopAll logging. "The camera light is still on"
   * cannot be answered from the code — the browser shows the indicator while
   * ANY track from that device is live, including ones opened outside this
   * module — so this says what WE still hold. If it reports nothing live and
   * the indicator is still on, the holder is elsewhere and this rules the
   * manager out.
   */
  debugState(): Record<string, unknown> {
    const of = (kind: MediaKind) => {
      const slot = slots[kind]
      return {
        hasTrack: !!slot.track,
        readyState: slot.track?.readyState ?? null,
        label: slot.track?.label ?? null,
        refCount: slot.refCount,
        retireScheduled: !!slot.retireTimer,
      }
    }
    return { audio: of('audio'), video: of('video') }
  },

  getTrack(kind: MediaKind): MediaStreamTrack | null {
    const track = slots[kind].track
    return track && track.readyState === 'live' ? track : null
  },

  /** Hear about device switches and endings. Returns an unsubscribe. */
  subscribe(kind: MediaKind, listener: TrackListener): () => void {
    slots[kind].listeners.add(listener)
    return () => {
      slots[kind].listeners.delete(listener)
    }
  },

  /**
   * Switch to a different physical device.
   *
   * The old track is retired only AFTER every consumer has moved across, so a
   * publisher's `replaceTrack()` never lands on a track that has already ended.
   */
  async setDevice(kind: MediaKind, deviceId: string | null): Promise<MediaStreamTrack | null> {
    const slot = slots[kind]
    const normalised = deviceId || null
    const current = slot.track && slot.track.readyState === 'live' ? slot.track : null

    // "No preference" is not a request to switch. Callers pass the stored
    // selection, which is empty until the user has ever picked a device — and
    // reading that as "change to the default" would re-open the camera on every
    // mount, which is the opposite of the point.
    if (!normalised) {
      slot.deviceId = null
      return current
    }

    // Already there. Compared against the track's real setting rather than the
    // remembered id, which is empty until the first capture resolves.
    if (current && current.getSettings().deviceId === normalised) return current

    const previousDeviceId = slot.deviceId
    slot.deviceId = normalised
    const want = { audio: kind === 'audio', video: kind === 'video' }

    /**
     * No live track — but that means two very different things.
     *
     * If nobody holds a claim, nothing is open because nothing wants to be:
     * record the choice and let the next `acquire` honour it.
     *
     * If somebody DOES hold a claim, the device died under us. That is not
     * hypothetical: moving from a Bluetooth headset to the built-in microphone
     * makes macOS drop the headset's input profile, which ends its track, which
     * empties this slot moments before the switch arrives. Returning null there
     * left the publisher holding an ended track and the room hearing silence —
     * with a log line claiming the switch had merely "failed", as though the old
     * microphone were still working. Open the newly chosen device instead.
     */
    if (!current) {
      if (slot.refCount === 0) return null

      trace(kind, 'REOPENING (setDevice: empty slot, claims outstanding)', { deviceId: normalised })
      const generation = slot.generation
      const stream = await captureWithFallback(want, false)
      const opened =
        (kind === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0]) ?? null
      if (!opened) {
        stream.getTracks().forEach((t) => t.stop())
        throw new Error(`no ${kind} track for the selected device`)
      }
      // Closed (left, or camera turned off) while this was opening — the same
      // moving ground `recoverAfterLoss` checks for. Adopting it would leave a
      // device running for a session that has gone.
      if (slot.generation !== generation) {
        stream.getTracks().forEach((t) => t.stop())
        return null
      }
      // `recoverAfterLoss` is reaching for this same empty slot from the other
      // direction — the death that emptied it is what woke both of us. Whichever
      // capture lands second wins, because it is the one the user asked for most
      // recently, but it has to turn the loser off on its way past: `adopt`
      // overwrites the slot, and an overwritten track nobody holds a reference
      // to is a device left running with its indicator light on.
      const raced = slot.track
      if (raced && raced !== opened) raced.stop()
      adopt(kind, opened)
      slot.deviceId = opened.getSettings().deviceId ?? normalised
      await notify(kind, opened)
      return opened
    }

    // Inherit the muted state, so the moment between devices is never live
    // while you are on mute.
    const wasEnabled = current.enabled

    // Cameras switch with no gap wherever two can be open at once.
    if (kind === 'video') {
      const switched = await switchCameraWhileRunning(current, normalised, previousDeviceId)
      if (switched !== 'close-first') return switched
      trace(kind, 'new camera refused alongside the old one — closing the old one first')
    }

    /**
     * RELEASE BEFORE ACQUIRE. This ordering is the fix, not a detail. Always
     * for a microphone; for a camera only when the gapless switch above could
     * not open the new one while the old was still running.
     *
     * We used to open the new device while the old one was still capturing and
     * stop the old one afterwards. macOS will not move a Bluetooth headset into
     * its input profile while another input capture is open, so the request was
     * answered with the device already running — the built-in microphone —
     * and, because the constraint was `ideal`, that counted as success. Closing
     * first gives the OS the room to switch.
     *
     * The cost is a real gap with no microphone in it, which is why the rollback
     * below is not optional: a switch that fails must leave you on the device
     * you had, not on nothing.
     */
    const generation = slot.generation
    current.stop()
    slot.track = null

    try {
      const stream = await captureWithFallback(want, false)
      const next =
        (kind === 'audio' ? stream.getAudioTracks()[0] : stream.getVideoTracks()[0]) ?? null
      if (!next) {
        stream.getTracks().forEach((t) => t.stop())
        throw new Error(`no ${kind} track for the selected device`)
      }

      // Trust nothing: check what we were GIVEN against what we asked for.
      //
      // `exact` should make this impossible, and this is here precisely because
      // "should be impossible" is what the last bug looked like from the inside
      // — every log line said the switch had worked.
      const actual = next.getSettings().deviceId
      if (actual && actual !== normalised) {
        next.stop()
        stream.getTracks().forEach((t) => t.stop())
        throw new Error(
          `browser returned a different ${kind} device than the one requested`,
        )
      }

      // Closed while the switch was in flight. Returned rather than thrown so
      // the rollback below does not reopen the old device either.
      if (slot.generation !== generation) {
        stream.getTracks().forEach((t) => t.stop())
        return null
      }

      next.enabled = wasEnabled
      adopt(kind, next)
      slot.deviceId = actual ?? normalised
      await notify(kind, next)
      return next
    } catch (err) {
      // Put back what was working. Best effort: if the old device has been
      // taken too, there is genuinely nothing to return to, and the original
      // failure is the one worth reporting.
      slot.deviceId = previousDeviceId
      // Nothing to put back for a session that has been closed.
      if (slot.generation !== generation) throw err
      try {
        const back = await captureWithFallback(want, true)
        const restored =
          (kind === 'audio' ? back.getAudioTracks()[0] : back.getVideoTracks()[0]) ?? null
        if (restored) {
          restored.enabled = wasEnabled
          adopt(kind, restored)
          slot.deviceId = restored.getSettings().deviceId ?? previousDeviceId
          await notify(kind, restored)
        }
      } catch {
        // Nothing to roll back to.
      }
      reportIssue({
        kind,
        reason: 'switch-failed',
        message:
          kind === 'audio'
            ? 'Could not switch to that microphone. Still using the previous one.'
            : 'Could not switch to that camera. Still using the previous one.',
        requestedDeviceId: normalised,
      })
      throw err
    }
  },

  /**
   * Re-shape the running capture.
   *
   * `applyConstraints` reconfigures a live track, so resolution and frame rate
   * change without re-opening the camera or flickering the permission state.
   * Not every device honours every constraint; a rejection leaves the track
   * exactly as it was, which is why this reports rather than retries.
   */
  async setVideoQuality(quality: VideoQuality): Promise<VideoQualityResult> {
    videoQuality = { ...quality }
    const track = deviceManager.getTrack('video')
    if (!track) return { applied: false, reason: 'no-track' }

    const want = videoConstraints(slots.video.deviceId)
    const wantW = (want.width as { max?: number })?.max ?? quality.width
    const wantH = (want.height as { max?: number })?.max ?? quality.height

    try {
      await track.applyConstraints(want)
    } catch {
      return { applied: false, reason: 'rejected', capabilities: cameraCapabilities(track) }
    }

    // applyConstraints resolving is NOT proof the camera did what was asked.
    // The width/height constraints are `ideal` + `max`, and `ideal` is a
    // preference a device is free to decline — declining resolves exactly like
    // succeeding. This used to `return true` right here, so the layer above
    // logged "video quality applied" for a 4K request that a 1080p webcam had
    // quietly floored, and the setting looked ignored because nothing ever
    // said it had not been honoured.
    //
    // Compare against what the track actually reports instead.
    const got = track.getSettings()
    const applied = got.width === wantW && got.height === wantH

    return {
      applied,
      reason: applied ? 'ok' : 'clamped-by-camera',
      requested: { width: wantW, height: wantH },
      actual: { width: got.width, height: got.height, frameRate: got.frameRate },
      capabilities: cameraCapabilities(track),
    }
  },

  /**
   * Re-shape the running microphone.
   *
   * `format` is optional so the two existing callers that only care about the
   * three switches keep working unchanged; when given, it is the preset's
   * capture format.
   */
  async setAudioProcessing(
    processing: AudioProcessing,
    denoising: boolean,
    format?: AudioFormat,
  ): Promise<boolean> {
    audioProcessing = { ...processing }
    noiseCancellation = denoising
    if (format) audioFormat = { ...format }
    const track = deviceManager.getTrack('audio')
    if (!track) return false
    try {
      await track.applyConstraints(audioConstraints(slots.audio.deviceId))
    } catch {
      // Judged by what the track reports below, not by whether this threw.
    }
    if (processingMatches(track)) return true
    return reopenMicrophone(track)
  },

  /**
   * Hear about a device problem: a stale selection dropped, or a switch that
   * did not take. Returns an unsubscribe.
   */
  onIssue(listener: (issue: DeviceIssue) => void): () => void {
    issueListeners.add(listener)
    return () => {
      issueListeners.delete(listener)
    }
  },

  /** What the running capture is actually doing, for logging and diagnostics. */
  describe(kind: MediaKind): MediaTrackSettings | null {
    return deviceManager.getTrack(kind)?.getSettings() ?? null
  },
}
